import { useState } from "react";
import type { RunItData, RunItView } from "../../api/growth";
import { Field, inputShell } from "../../components/Field";
import { GateFooter } from "../../components/GateFooter";
import { Guide } from "../../components/Guide";
import { StepChecklist } from "../../components/StepChecklist";
import { OptionTile } from "../../components/OptionTile";
import { Pill } from "../../components/Pill";
import { ScreenHeader } from "../../components/ScreenHeader";
import { Section } from "../../components/Section";
import { numberOrNull, wordCount } from "../../lib/format";

interface RunItScreenProps {
  view: RunItView;
  clientChecklist: { k: string; t: string }[];
  busy: boolean;
  onChange: (data: RunItData) => void;
  onComplete: () => void;
}

function Counter({ text, min }: { text: string; min: number }) {
  const n = wordCount(text);
  return (
    <span className={`text-[12.5px] ${n < min ? "text-negative" : "text-text-muted"}`}>
      {n} words (at least {min})
    </span>
  );
}

/** Step 5: Run It. Done with a real, live gig. */
export function RunItScreen({
  view,
  clientChecklist,
  busy,
  onChange,
  onComplete,
}: RunItScreenProps) {
  const [data, setData] = useState<RunItData>(view.step);
  const [gigCheck, cmCheck, repCheck] = view.checks;

  const update = (next: RunItData) => {
    setData(next);
    onChange(next);
  };
  const gig = (patch: Partial<RunItData["gig"]>) =>
    update({ ...data, gig: { ...data.gig, ...patch } });
  const rep = (patch: Partial<RunItData["reputation"]>) =>
    update({ ...data, reputation: { ...data.reputation, ...patch } });
  const checked = new Set(data.checklist);

  return (
    <div>
      <ScreenHeader
        kicker="Step 5 · Run It · while earning"
        title="Turn a first gig into a repeat client."
        sub="Do this step with a real, live gig. If your first gig isn't in yet, keep bidding and come back when it is. Progress saves as you go. Nothing here should be made up."
      />
      <Guide guideKey="gig5" done={view.done} />
      <StepChecklist gate={view.gate} done={view.done} />
      <Section title="1. Your live gig" status={<Pill ok={Boolean(gigCheck?.pass)} />}>
        <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
          <Field label="Client (name or initials)" htmlFor="g5-client">
            <input
              id="g5-client"
              className={inputShell}
              value={data.gig.client}
              onChange={(e) => gig({ client: e.target.value })}
            />
          </Field>
          <Field label="Where it came from" htmlFor="g5-source">
            <select
              id="g5-source"
              className={inputShell}
              value={data.gig.source}
              onChange={(e) => gig({ source: e.target.value })}
            >
              <option value="">Choose...</option>
              {view.sources.map((source) => (
                <option key={source}>{source}</option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="What you're delivering" htmlFor="g5-what">
          <input
            id="g5-what"
            className={inputShell}
            placeholder="e.g. 5 Instagram reels for a restaurant's opening week"
            value={data.gig.what}
            onChange={(e) => gig({ what: e.target.value })}
          />
        </Field>
        <Field label="Gig value (USD)" htmlFor="g5-value">
          <input
            id="g5-value"
            type="number"
            min={0}
            className={`${inputShell} max-w-[220px]`}
            value={data.gig.value ?? ""}
            onChange={(e) => gig({ value: numberOrNull(e.target.value) })}
          />
        </Field>
      </Section>
      <Section
        title="2. Client-management checklist"
        hint="Check off each one as it genuinely happens on this gig."
        status={<Pill ok={Boolean(cmCheck?.pass)} />}
      >
        <div className="flex flex-col gap-2">
          {clientChecklist.map((item) => (
            <OptionTile
              key={item.k}
              selected={checked.has(item.k)}
              title={item.t}
              onToggle={() => {
                const next = new Set(checked);
                if (next.has(item.k)) next.delete(item.k);
                else next.add(item.k);
                update({ ...data, checklist: Array.from(next) });
              }}
            />
          ))}
        </div>
      </Section>
      <Section
        title="3. Reputation and retainer"
        hint="Happy clients rarely leave a review unless asked. And a repeat client is worth more than a new one."
        status={<Pill ok={Boolean(repCheck?.pass)} />}
      >
        <Field
          label="Your review request message"
          htmlFor="r5-review"
          hint={<Counter text={data.reputation.reviewMsg} min={25} />}
        >
          <textarea
            id="r5-review"
            rows={3}
            className={inputShell}
            placeholder="Thank them, name one thing you enjoyed about the project, and ask for a short review with a direct link."
            value={data.reputation.reviewMsg}
            onChange={(e) => rep({ reviewMsg: e.target.value })}
          />
        </Field>
        <OptionTile
          selected={data.reputation.reviewSent}
          title="I've sent it to the client"
          onToggle={() => rep({ reviewSent: !data.reputation.reviewSent })}
        />
        <div className="mt-4">
          <Field
            label="Your repeat or retainer pitch"
            htmlFor="r5-ret"
            hint={<Counter text={data.reputation.retainer} min={40} />}
          >
            <textarea
              id="r5-ret"
              rows={4}
              className={inputShell}
              placeholder="Offer ongoing help: what you'd do each month, the result it drives, a monthly price, and an easy yes/no question."
              value={data.reputation.retainer}
              onChange={(e) => rep({ retainer: e.target.value })}
            />
          </Field>
        </div>
        <OptionTile
          selected={data.reputation.retainerSent}
          title="I've sent it to the client"
          onToggle={() => rep({ retainerSent: !data.reputation.retainerSent })}
        />
      </Section>
      <GateFooter
        gate={view.gate}
        done={view.done}
        label="Complete Step 5"
        next={{ to: "/gig/6", label: "Continue to Step 6" }}
        busy={busy}
        onComplete={onComplete}
      />
    </div>
  );
}
